// ===========================================
// Business Card Recognition Service
// ===========================================
// Calls the crm_parseBusinessCard Cloud Function
// to extract data from business card photos via Gemini AI.

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

// ===========================================
// Types
// ===========================================

export interface BusinessCardData {
  companyName: string;
  companyType: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  website: string;
  contactFirstName: string;
  contactLastName: string;
  contactTitle: string;
  contactPhone: string;
  contactEmail: string;
}

export interface ParseBusinessCardResult {
  success: boolean;
  data: BusinessCardData;
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
  model: string;
}

// ===========================================
// Helpers
// ===========================================

/**
 * Convert a File to a base64 string (without data URI prefix)
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data URI prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// ===========================================
// API
// ===========================================

/**
 * Parse a business card image using Gemini AI.
 * @param file - The image file (JPEG, PNG, WebP)
 * @param companyTypes - Valid company types to classify into
 * @returns Parsed business card data with confidence and warnings
 */
export async function parseBusinessCard(
  file: File,
  companyTypes: string[],
): Promise<ParseBusinessCardResult> {
  const base64Image = await fileToBase64(file);

  const callable = httpsCallable<
    { base64Image: string; mimeType: string; companyTypes: string[] },
    ParseBusinessCardResult
  >(functions, 'crm_parseBusinessCard');

  const result = await callable({
    base64Image,
    mimeType: file.type || 'image/jpeg',
    companyTypes,
  });

  return result.data;
}
